import React, {useState, useCallback, useRef} from 'react';
import {View} from 'react-native';

import sy from '~/styles';
import {Text, Dialog} from '~/components/controls';
import Format from '~/lib/format';

import FileOpenIcon from '~/images/md-icons/file_open/materialicons/24px.svg';
import UploadFileIcon from '~/images/md-icons/upload_file/materialicons/24px.svg';
import ErrorIcon from '~/images/md-icons/error/materialicons/24px.svg';
import PropTypes from 'prop-types';

const UPLOAD_ERRORS = {
  FLEET_INVOICE_NUMBER_MISMATCH:
    'Opgegeven factuurnummer komt niet overeen met PDF. Controleer de factuur en het opgegeven nummer.',
  TOTAL_AMOUNT_MISMATCH:
    'Factuurbedrag komt niet overeen met de opdracht. Controleer de factuur en upload deze opnieuw.',
};

const InvoiceUpload = ({visible, lessor, onDismiss, onOK}) => {
  const fileRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [errors, setErrors] = useState([]);

  const handleFile = useCallback((e) => {
    e.persist();
    const fileList = e.target.files;

    if (fileList.length) {
      const pdfFile = fileList[0];

      if (pdfFile.type.indexOf('application/pdf') !== 0) {
        console.log('File is not an pdf file', pdfFile);
        return;
      }

      const reader = new FileReader();
      reader.onload = function (event) {
        setPdf({
          data: event.target.result,
          name: pdfFile.name,
          length: pdfFile.size,
        });
        e.target.value = '';
      };
      reader.readAsDataURL(pdfFile);
    }
  }, []);

  return (
    <Dialog
      visible={visible}
      title="Factuur uploaden"
      buttons={[
        {text: 'Terug', onPress: onDismiss},
        {
          text: 'Factureren',
          disabled: !pdf,
          textStyle: {color: '#972727'},
          onPress: async () => {
            try {
              await onOK(pdf.data);
            } catch (e) {
              setErrors(e);
              setPdf(null);
            }
          },
        },
      ]}
      options={{
        hideDividers: true,
        noPaddingContent: true,
      }}
      onDismiss={onDismiss}>
      <View style={[sy['px-6'], sy['py-4'], sy['gap-2']]}>
        <Text>
          Let op: facturatie aan {lessor}. Upload hier een PDF van de factuur.
        </Text>
        <View>
          {pdf ? (
            <View
              style={[
                sy['p-4'],
                sy['gap-2'],
                sy['rounded-lg'],
                {
                  borderStyle: 'dotted',
                  borderWidth: 1,
                  borderColor: '#4a4a49',
                },
              ]}>
              <FileOpenIcon style={{alignSelf: 'center'}} fill="#4a4a49" />
              <Text style={[sy['truncate'], sy['text-center']]}>
                {pdf.name.slice(0, pdf.name.lastIndexOf('.'))}
              </Text>
              <Text style={[sy['text-lightgray'], sy['text-center']]}>
                {Format.number(pdf.length / 1024, 0)}
                &nbsp;kb&nbsp;&middot;&nbsp;pdf
              </Text>
            </View>
          ) : (
            <button
              type="button"
              ref={dropZoneRef}
              style={{
                padding: 16,
                borderRadius: 8,
                borderStyle: 'dotted',
                borderWidth: 1,
                borderColor: '#4a4a49',
                width: '100%',
                background: 'transparent',
              }}
              onClick={() => fileRef.current?.click()}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileRef.current?.click();
                }
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                fileRef.current?.click();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const items = e.dataTransfer.items;
                if (items) {
                  const item = items[0];

                  if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file.type !== 'application/pdf') {
                      console.log('File is not an pdf file', file);
                      return;
                    }

                    const reader = new FileReader();
                    reader.onload = function (event) {
                      setPdf({
                        data: event.target.result,
                        name: file.name,
                        length: file.size,
                      });
                    };
                    reader.readAsDataURL(file);
                  }
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                dropZoneRef.current.style.backgroundColor = '#f2f2f2';
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                dropZoneRef.current.style.backgroundColor = 'transparent';
              }}>
              <View style={[sy['items-center'], sy['gap-2']]}>
                <UploadFileIcon fill="#FF6C00" />
                <Text style={[sy['text-center'], {color: '#FF6C00'}]}>
                  Sleep hier heen
                  <br />
                  of
                </Text>
                <label
                  htmlFor="file-upload"
                  style={{
                    borderRadius: 8,
                    padding: 12,
                    alignItems: 'center',
                    border: '1px solid #FF6C00',
                    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 1.5px 5px 0.5px',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                  }}>
                  <Text style={{color: '#FF6C00'}}>Kies bestand</Text>
                </label>
                <input
                  ref={fileRef}
                  name="file-upload"
                  style={{display: 'none'}}
                  type="file"
                  accept="application/pdf"
                  onInput={handleFile}
                />
              </View>
            </button>
          )}
          {errors.length > 0 && (
            <View style={[sy['pt-4'], sy['gap-4'], sy['flex-row']]}>
              <ErrorIcon style={{alignSelf: 'center'}} fill="#972727" />
              <View style={[sy['flex-1'], sy['gap-2']]}>
                {errors.map((error) => (
                  <Text key={error} style={[sy['text-error']]}>
                    {UPLOAD_ERRORS[error] ?? error}
                  </Text>
                ))}
              </View>
            </View>
          )}
        </View>
      </View>
    </Dialog>
  );
};
InvoiceUpload.propTypes = {
  visible: PropTypes.any,
  lessor: PropTypes.any,
  onDismiss: PropTypes.any,
  onOK: PropTypes.any,
};

export default InvoiceUpload;
